import { useStore } from '@tanstack/react-form';
import { CircleCheckBig, Clock } from 'lucide-react';

import Transition from '@/components/Common/Transition';
import { ScheduleSelect } from '@/components/ScheduleSelect/ScheduleSelect';
import { Input } from '@/components/shadcn/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/ui/select';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@/components/shadcn/ui/toggle-group';
import { periodicFormOptions } from '@/entities/(notification)/periodic';
import { withForm } from '@/shared/ui/form';
import {
  type WORKFLOW_COMPARE_DELTA,
  WORKFLOW_PERIODIC_BASIS,
  WORKFLOW_PERIODIC_TRIGGER_TYPE,
  WorkflowiCompareDeltaDic,
  WorkflowPeriodicBasisDic,
} from '@/types/workflowConst';

import SpecificPeriodicBasisSelect from '../WorkflowDetail/periodic/SpecificPeriodicBasisSelect';

const basisOptions = Object.keys(WorkflowPeriodicBasisDic).map((key) => {
  const value = Number(key) as WORKFLOW_PERIODIC_BASIS;
  return {
    value: value,
    label: WorkflowPeriodicBasisDic[value],
  };
});

export const PeriodicTriggerSection = withForm({
  ...periodicFormOptions,
  render: function PeriodicTriggerSection({ form }) {
    const basis = useStore(form.store, (state) => state.values.basis);
    const all = useStore(form.store, (state) => state.values.all);

    return (
      <div className="flex flex-col gap-5 rounded-lg border p-5">
        {/* 헤더 */}
        <div className="flex items-center gap-2">
          <CircleCheckBig className="size-5 text-green-600" />
          <span className="text-sm font-semibold">Trigger</span>
        </div>

        {/* Basis 선택 */}
        <div className="grid grid-cols-[120px_1fr] items-center gap-3">
          <span className="text-sm text-muted-foreground">Notify based on</span>
          <div className="flex items-center gap-3">
            <form.AppField
              name="basis"
              children={(field) => (
                <Select
                  value={
                    field.state.value !== undefined
                      ? String(field.state.value)
                      : undefined
                  }
                  onValueChange={(value) => {
                    const basisValue = Number(value) as WORKFLOW_PERIODIC_BASIS;
                    field.handleChange(basisValue);
                    // Topic인 경우 all을 false로, 그 외에는 true로
                    if (basisValue === WORKFLOW_PERIODIC_BASIS.TOPIC) {
                      form.setFieldValue('all', false);
                    } else {
                      form.setFieldValue('all', true);
                    }
                    form.setFieldValue('basis_list', []);
                  }}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {basisOptions.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={String(option.value)}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {/* All / Specific 토글 (Topic이 아닌 경우에만) */}
            {basis !== WORKFLOW_PERIODIC_BASIS.TOPIC && (
              <form.AppField
                name="all"
                children={(field) => (
                  <ToggleGroup
                    type="single"
                    variant="outline"
                    size="sm"
                    value={String(field.state.value)}
                    onValueChange={(value) => {
                      if (!value) return;
                      field.handleChange(value === 'true');
                    }}
                  >
                    <ToggleGroupItem value="true">All</ToggleGroupItem>
                    {basis !== WORKFLOW_PERIODIC_BASIS.FEEDBACK && (
                      <ToggleGroupItem value="false">Specific</ToggleGroupItem>
                    )}
                  </ToggleGroup>
                )}
              />
            )}
            {/* Specific 선택 */}
            <Transition status={!all}>
              <form.AppField
                name="basis_list"
                children={(field) => (
                  <SpecificPeriodicBasisSelect
                    value={field.state.value}
                    onChange={field.handleChange}
                    basis={basis}
                  />
                )}
              />
            </Transition>
            <span className="text-sm font-medium">
              measured by feedback count
            </span>
          </div>
        </div>

        {/* Trigger 조건 (basis 선택된 경우에만) */}
        <Transition status={!!basis}>
          <div className="flex flex-col gap-4">
            {/* Trigger when */}
            <div className="grid grid-cols-[120px_1fr] items-center gap-3">
              <span className="text-sm text-muted-foreground">
                Trigger when
              </span>
              <div className="flex items-center gap-3">
                {/* Percentage input (triggerType이 PERCENTAGE인 경우) */}
                <form.Subscribe selector={(s) => s.values.triggerType}>
                  {(triggerType) =>
                    triggerType === WORKFLOW_PERIODIC_TRIGGER_TYPE.PERCENTAGE && (
                      <form.AppField
                        name="percentage"
                        children={(field) => (
                          <Input
                            className="w-16"
                            type="number"
                            value={field.state.value ?? ''}
                            onChange={(e) => {
                              const value = Number(e.target.value);
                              if (value >= 0) {
                                field.handleChange(value);
                              }
                            }}
                          />
                        )}
                      />
                    )
                  }
                </form.Subscribe>

                {/* triggerType 선택 */}
                <form.AppField
                  name="triggerType"
                  children={(field) => (
                    <Select
                      value={field.state.value}
                      onValueChange={(v) => {
                        const triggerType = v as WORKFLOW_PERIODIC_TRIGGER_TYPE;
                        field.handleChange(triggerType);
                        if (triggerType === WORKFLOW_PERIODIC_TRIGGER_TYPE.NUMERICAL) {
                          form.setFieldValue('percentage', undefined);
                        } else {
                          form.setFieldValue('percentage', 20);
                        }
                      }}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.values(WORKFLOW_PERIODIC_TRIGGER_TYPE).map(
                          (value) => (
                            <SelectItem key={value} value={value}>
                              {value}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  )}
                />

                {/* Delta 선택 */}
                <form.AppField
                  name="delta"
                  children={(field) => (
                    <Select
                      value={
                        field.state.value !== undefined
                          ? String(field.state.value)
                          : undefined
                      }
                      onValueChange={(v) =>
                        field.handleChange(Number(v) as WORKFLOW_COMPARE_DELTA)
                      }
                    >
                      <SelectTrigger className="w-20">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(WorkflowiCompareDeltaDic).map(
                          ([key, value]) => (
                            <SelectItem key={key} value={key}>
                              {value}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  )}
                />

                <span className="text-sm">exceeding by</span>

                {/* Numerical input */}
                <form.AppField
                  name="numerical"
                  children={(field) => (
                    <Input
                      className="w-20"
                      type="number"
                      value={field.state.value ?? ''}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        if (value >= 0) {
                          field.handleChange(value);
                        }
                      }}
                    />
                  )}
                />
              </div>
            </div>

            {/* Schedule */}
            <div className="grid grid-cols-[120px_1fr] items-center gap-3">
              <div className="flex items-center gap-1.5">
                <Clock className="size-4" />
                <span className="text-sm text-muted-foreground">When</span>
              </div>
              <form.AppField
                name="schedule"
                children={(field) => (
                  <ScheduleSelect
                    schedule={field.state.value}
                    setSchedule={field.handleChange}
                  />
                )}
              />
            </div>
          </div>
        </Transition>
      </div>
    );
  },
});
